/* eslint semi: 1 */
import { inject } from 'vue';
import type { AxiosError, AxiosInstance } from 'axios';
import type ApiError from '@/globals/api-error';
import type { components } from '@base-shared/types/media.openapi';

// NOTE: Incomplete type for media resource item openapi spec
type MediaItem = components['schemas']['MediaItem'] & Record<string, unknown>;
type MediaQueryDir = components['parameters']['dir'];

/**
 * Payload sent to POST /media to register a new upload and
 * obtain the S3 pre-signed POST.
 */
export interface CreateMediaInstancePayload {
  name: string
  fileExtension: string
  mimeType: string
  fileSize: number
  uid: string
  hash: string
  alert: boolean
  tags?: string[]
  led?: string
}

export interface MediaInstanceResult {
  status: 'success' | 'error'
  responseData: MediaItem
  requestData: CreateMediaInstancePayload
}

export interface S3UploadResult {
  status: 'success' | 'error'
  message: string
  resourceId: string
}

/**
 * Composable providing the API calls needed at each stage of the file upload pipeline:
 * create media instance -> upload to S3 -> poll conversion -> save schedule / update metadata.
 *
 * All functions are stateless wrappers around the injected Axios `$api` instance,
 * No component or queue state is mutated here - callers own state updates.
 */
export function useFileUploadApi () {
  const $api = inject('$api') as AxiosInstance;
  const $apiError = inject('$apiError') as typeof ApiError;

  /** Builds the `&dir=` query string fragment from an optional orientation string. */
  function dirParam (orientation?: string | null): string {
    return orientation ? `&dir=${orientation.toLowerCase()}` : '';
  }

  /**
   * Wraps an Axios call with the app-wide 401-retry pattern.
   * The returned Promise stays pending on 401 until the session token is refreshed
   * and `$apiError` re-issues the request. All other errors reject the Promise.
   */
  function withRetry<T> (attempt: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const run = () => {
        attempt()
          .then(resolve)
          .catch(error => {
            $apiError.handleUnauthorizedError(error, { originalRequest: run });
            if (error?.response?.status !== 401) reject(error);
          });
      };
      run();
    });
  }

  /**
   * POST /media - registers a new upload slot and returns the S3 pre-signed POST data.
   * On duplicate filename the backend responds with `name === 'DuplicateResourceNameError'`;
   * on other server errors `status === 'error'` is returned rather than thrown.
   * 401 responses are transparently retried once the session token is refreshed.
   */
  function createMediaInstance (
    payload: CreateMediaInstancePayload,
    orientation?: MediaQueryDir | null
  ): Promise<MediaInstanceResult> {
    const url = `${CONFIG.API_BASE_URL}/media?showPost=1&width=1280&use_qsa=1${dirParam(orientation)}`;
    return withRetry(() =>
      $api.post(url, payload).then(r => ({
        status: 'success' as const,
        responseData: r.data as MediaItem,
        requestData: payload
      }))
    ).catch(error => ({
      status: 'error' as const,
      responseData: (error?.response?.data ?? error) as MediaItem,
      requestData: payload
    }));
  }

  /**
   * POST /media?force=1 or ?replace=1 - resolves a duplicate-file conflict by
   * either copying or replacing the existing asset.
   * 401 is reported but not retried (the sequential batch loop makes transparent retry impractical).
   */
  async function resolveMediaInstance (
    payload: CreateMediaInstancePayload,
    mode: 'copy' | 'replace'
  ): Promise<MediaInstanceResult> {
    const modeParam = mode === 'copy' ? '&force=1' : '&replace=1';
    const url = `${CONFIG.API_BASE_URL}/media?showPost=1&width=1280&use_qsa=1${modeParam}`;
    try {
      const response = await $api.post(url, payload);
      return {
        status: 'success',
        responseData: response.data as MediaItem,
        requestData: payload
      };
    } catch (error: unknown) {
      $apiError.handleUnauthorizedError(error);
      const axiosError = error as { response?: { data: MediaItem } };
      return {
        status: 'error',
        responseData: axiosError?.response?.data ?? {} as MediaItem,
        requestData: payload
      };
    }
  }

  /**
   * GET /media/:resourceId - fetches the full media item details after conversion.
   * 401 responses are transparently retried.
   */
  async function fetchMediaDetails (
    resourceId: string,
    orientation?: MediaQueryDir | null
  ): Promise<MediaItem> {
    const requestParams: Record<string, unknown> = { use_qsa: 1, width: 424 };
    if (orientation) requestParams.dir = orientation;
    return withRetry(() => $api
      .get(`${CONFIG.API_BASE_URL}/media/${resourceId}`, { params: requestParams })
      .then(r => r.data as MediaItem));
  }

  /**
   * POST <s3url> - uploads the raw file binary using the pre-signed POST fields returned
   * by the backend. The Authorization header is stripped; S3 does not accept it.
   * Always resolves (never rejects) - failures are surfaced via `status === 'error'`.
   */
  function uploadToS3 (
    url: string,
    fields: Record<string, string>,
    rawFile: File,
    filename: string
  ): Promise<S3UploadResult> {
    const resourceId = fields['x-amz-meta-resourceid'] ?? '';
    const formData = new FormData();
    for (const name in fields) formData.append(name, fields[name]);
    formData.append('file', rawFile, filename);

    return $api
      .post(url, formData, {
        transformRequest: [(data: unknown, headers: Record<string, string>) => {
          delete headers.Authorization;
          headers['Content-Type'] = 'multipart/form-data';
          return data;
        }]
      })
      .then(() => ({ status: 'success' as const, message: '', resourceId }))
      .catch((error: { message: string }) => ({
        status: 'error' as const,
        message: `Error in S3 upload : ${error.message}`,
        resourceId
      }));
  }

  /**
   * PUT /media/:resourceId {status:'failed'} - notifies the backend that an S3 upload failed.
   * Fire-and-forget: no return value or error handling (best-effort notification).
   */
  function notifyUploadFailed (resourceId: string, reason: string): void {
    $api.put(`${CONFIG.API_BASE_URL}/media/${resourceId}`, { status: 'failed', reason });
  }

  /**
   * PUT /media/:resourceId - sends deferred metadata (tags, name, etc.) that the user
   * edited while the file was still converting.
   * 401 responses are transparently retried.
   */
  function updateMediaData (
    resourceId: string,
    requestData: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    return withRetry(() =>
      $api.put(`${CONFIG.API_BASE_URL}/media/${resourceId}`, requestData)
        .then(r => r.data as Record<string, unknown>)
    );
  }

  /**
   * PUT /media/:resourceId?scheduleId=... - saves the schedule playtime entry for a
   * newly-converted file that was uploaded from the schedule page.
   * 401 responses are transparently retried.
   */
  function saveSchedule (
    resourceId: string,
    scheduleId: string,
    mediaData: Record<string, unknown>,
    orientation?: string | null
  ): Promise<Record<string, unknown>> {
    const url = `${CONFIG.API_BASE_URL}/media/${resourceId}?scheduleId=${scheduleId}${dirParam(orientation)}`;
    return withRetry(() =>
      $api.put(url, mediaData).then(r => r.data as Record<string, unknown>)
    );
  }

  /**
   * GET /media/:resourceId/status?... - Media item preview status URL
   * Derived from the file upload item resource id, fetches
   * the current status of the media item.
   * @param resourceId - Resource ID of the media item
   * @returns Promise resolving to an object with success flag, error (if any), and response data (MediaItem)
   */
  async function fetchStatusFromId (resourceId: string): Promise<{
    success: boolean;
    error?: AxiosError | Error;
    response?: MediaItem
  }> {
    if (!resourceId) {
      return { success: false, error: new Error('Missing resource ID for media item') };
    }

    try {
      const response = await $api.get(`${CONFIG.API_BASE_URL}/media/${resourceId}/status?width=1280&dir=vertical`);
      return {
        success: true,
        response: response.data
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      $apiError.handleUnauthorizedError(axiosError);
      return {
        success: false,
        error: axiosError
      };
    }
  }

  return {
    createMediaInstance,
    resolveMediaInstance,
    fetchMediaDetails,
    uploadToS3,
    notifyUploadFailed,
    updateMediaData,
    saveSchedule,
    fetchStatusFromId
  };
}
